
interface UrlResolverLink {

    isApplicable(url: string): boolean;

    resolve(url: string): string;

}

/**
 * Makes the URL relative by adding a forward-slash as needed
 */
class RelativeUrlResolverLink implements UrlResolverLink {

    public static REGEXP: RegExp = /^\/(?!\/)/ig;

    isApplicable(url: string): boolean {
        return !!url && !url.match(RelativeUrlResolverLink.REGEXP);
    }

    resolve(url: string): string {
        return "/" + url;
    }

}

/**
 * Injects the servlet into the URL
 */
class ServletUrlResolverLink implements UrlResolverLink {

    public static SERVLET: string = "WebDispatch"; //-- using KryptonApp.servletName has initialization race issues
    public static HELP: string = "help"; //-- using KryptonApp.servletName has initialization race issues
    // public static REGEXP: RegExp = new RegExp("(?<!\/)\/?" + ServletUrlResolverLink.SERVLET, "ig"); negative lookbehind has issues in ie11
    public static REGEXP: RegExp = new RegExp("^\/?" + ServletUrlResolverLink.SERVLET, "ig");
    public static REGEXP_HELP = new RegExp("\/" + ServletUrlResolverLink.HELP + "\/", "ig");
    public static REGEXP_HTML = new RegExp('.html$');

    isApplicable(url: string): boolean {

        return !!url && !url.match(ServletUrlResolverLink.REGEXP) && !url.match(ServletUrlResolverLink.REGEXP_HELP) && !url.match(ServletUrlResolverLink.REGEXP_HTML);
    }

    /**
     * Add the servlet to the URL if it is not present
     * @param url
     */
    resolve(url: string): string {
        return "/" + ServletUrlResolverLink.SERVLET + url;
    }

}

/**
 * Injects the servlet into the URL
 */
class PublicServletUrlResolverLink implements UrlResolverLink {

    public static SERVLET: string = "Public"; //-- using KryptonApp.servletName has initialization race issues
    // public static REGEXP: RegExp = new RegExp("(?<!\/)\/?" + PublicServletUrlResolverLink.SERVLET, "ig"); negative lookbehind has issues in ie11
    public static REGEXP: RegExp = new RegExp("^\/?" + PublicServletUrlResolverLink.SERVLET, "ig");
    public static REGEXP_HTML = new RegExp('.html');

    isApplicable(url: string): boolean {

        return !!url && !url.match(PublicServletUrlResolverLink.REGEXP) && !url.match(PublicServletUrlResolverLink.REGEXP_HTML);
    }

    /**
     * Add the servlet to the URL if it is not present
     * @param url
     */
    resolve(url: string): string {
        return "/" + PublicServletUrlResolverLink.SERVLET + url;
    }

}

/**
 * Removes the domain from the URL
 */
class DomainUrlResolverLink implements UrlResolverLink {

    public static REGEXP: RegExp = RegExp("https?://[^/]*");

    isApplicable(url: string): boolean {
        return !!url && !!url.match(DomainUrlResolverLink.REGEXP);
    }

    /**
     * Removes the leading https://*.sempra.com from the url
     * @param url
     */
    resolve(url: string): string {
        let result = url.replace(DomainUrlResolverLink.REGEXP, "");
        return result.indexOf("/") == 0 ? result : "/" + result;
    }
}

/**
 * Strips the preceeding ghostLab IP and returns the application URL
 */
//@ts-ignore
class GhostLabUrlResolverLink implements UrlResolverLink {

    public static REGEXP: RegExp = /http(s)?:\/\/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(:\d{1,4})?(\/)?/ig; //ip address pattern preceeding the actual URL

    isApplicable(url: string): boolean {
        return !!url && !!url.match(GhostLabUrlResolverLink.REGEXP);
    }

    /**
     * Removes the preceeding IP address URL that is the proxy for GhostLab
     * @param url
     */
    resolve(url: string): string {
        let result = GhostLabUrlResolverLink.REGEXP.exec(url);
        if (!result) return url;
        let ipAddressUrl = result[0], managedUrl = url.substring(ipAddressUrl.length);
        if (managedUrl) return managedUrl;
        return url;
    }

}

class CSRFUrlResolverLink implements UrlResolverLink {

    public static CSRF_TOKEN_NAME = "AUTH_TOKEN";
    public static CSRF_TOKEN_VALUE;
    public static CSRF_TOKEN_REGEX = new RegExp("(\\?)?(\\&)?" + CSRFUrlResolverLink.CSRF_TOKEN_NAME + "" + "=\\w{4}-\\w{4}-\\w{4}-\\w{4}-\\w{4}-\\w{4}-\\w{4}-\\w{4}", "ig");

    constructor() {
        (async () => {
            if (!CSRFUrlResolverLink.CSRF_TOKEN_VALUE)
                CSRFUrlResolverLink.CSRF_TOKEN_VALUE = window["getCsrfTokenValue"] && window["getCsrfTokenValue"]();
            return CSRFUrlResolverLink.CSRF_TOKEN_VALUE;
        })();
    }

    isApplicable(url: string): boolean {
        return !!url && !url.match(CSRFUrlResolverLink.CSRF_TOKEN_REGEX);
    }

    resolve(url: string): string {
        let bindingChar = url.indexOf("?") >= 0 ? "&" : "?";
        return url + bindingChar + (window["getCsrfToken"] && window["getCsrfToken"]());
    }

}

abstract class UrlResolverChain implements UrlResolverLink {

    abstract isApplicable(url: string): boolean;

    abstract getResolverChain(): UrlResolverLink[];

    resolve(fullUrl: string): string {
        // Some pages has openModule with urls that has some anchors, so we need to make sure the CSRF token is added correctly 
        // to the actual url and anchors are appended at the end
        let url = fullUrl.split("#", 1)[0];
        let hash = fullUrl.slice(url.length);
        for (let i = 0; i < this.getResolverChain().length; i++) {
            let urlResolver = this.getResolverChain()[i];
            if (urlResolver.isApplicable(url)) {
                url = urlResolver.resolve(url);
            }
        }
        return url + hash;
    }

}

class RelativeUrlResolver extends UrlResolverChain {

    // order matters
    private static UrlResolverLinks: UrlResolverLink[] = [new DomainUrlResolverLink(), new RelativeUrlResolverLink(), new ServletUrlResolverLink(), new CSRFUrlResolverLink()];

    getResolverChain(): UrlResolverLink[] {
        return RelativeUrlResolver.UrlResolverLinks;
    }

    isApplicable(url: string): boolean {
        return !!url;
    }

}

class PublicRelativeUrlResolver extends UrlResolverChain {

    // order matters
    private static UrlResolverLinks: UrlResolverLink[] = [new DomainUrlResolverLink(), new RelativeUrlResolverLink(), new PublicServletUrlResolverLink()];

    getResolverChain(): UrlResolverLink[] {
        return PublicRelativeUrlResolver.UrlResolverLinks;
    }

    isApplicable(url: string): boolean {
        return !!url;
    }

}

export class UrlResolver {

    public static RELATIVE: RelativeUrlResolver = new RelativeUrlResolver();
    public static PUBLIC_RELATIVE: PublicRelativeUrlResolver = new PublicRelativeUrlResolver();
    public static stripCsrfToken(url: string) {
        return url && url.replace(CSRFUrlResolverLink.CSRF_TOKEN_REGEX, "");
    }

}

// duplicated from common.ts
function escapeRegex(string: string) {
    return string && string.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
}
